import type {
  FunnelOverview,
  RecentSignup,
  AbandonedSession,
  DailyTrend,
  SourceBreakdown,
  SessionJourney,
  GhlConnectionStatus,
} from '../types';

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8080';

class MarketingService {
  private accessToken: string | null = null;

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  setAccessToken(token: string): void {
    this.accessToken = token;
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('marketing_access_token', token);
    }
  }

  getAccessToken(): string | null {
    if (this.accessToken) return this.accessToken;
    if (typeof window !== 'undefined') {
      this.accessToken = sessionStorage.getItem('marketing_access_token');
    }
    return this.accessToken;
  }

  clearSession(): void {
    this.accessToken = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('marketing_access_token');
    }
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // ============================================
  // HTTP HELPERS
  // ============================================

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      this.clearSession();
      if (typeof window !== 'undefined') {
        window.location.href = '/marketing/login';
      }
      throw new Error('Session expired');
    }

    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.message || 'Request failed');
    }

    // Backend wraps responses: { statusCode, data: { success, data } }
    // Unwrap to return the inner data
    const wrapped = json?.data;
    if (wrapped && typeof wrapped === 'object' && 'data' in wrapped) {
      return wrapped.data as T;
    }
    return (wrapped ?? json) as T;
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  async login(
    email: string,
    password: string
  ): Promise<{
    accessToken: string;
    expiresIn: number;
    user: { id: string; name: string; email: string };
  }> {
    return this.request('/api/v1/marketing/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // ============================================
  // ANALYTICS
  // ============================================

  async getFunnelOverview(
    startDate?: string,
    endDate?: string
  ): Promise<FunnelOverview> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    return this.request(`/api/v1/marketing/analytics/funnel-overview${query ? `?${query}` : ''}`);
  }

  async getRecentSignups(
    limit?: number,
    offset?: number
  ): Promise<RecentSignup[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();
    const result = await this.request<{ signups: RecentSignup[] }>(`/api/v1/marketing/analytics/recent-signups${query ? `?${query}` : ''}`);
    return result?.signups ?? [];
  }

  async getAbandoned(
    limit?: number,
    offset?: number
  ): Promise<AbandonedSession[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();
    const result = await this.request<{ sessions: AbandonedSession[] }>(`/api/v1/marketing/analytics/abandoned${query ? `?${query}` : ''}`);
    return result?.sessions ?? [];
  }

  async getTrends(
    startDate?: string,
    endDate?: string
  ): Promise<DailyTrend[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    const result = await this.request<{ trends: DailyTrend[] }>(`/api/v1/marketing/analytics/trends${query ? `?${query}` : ''}`);
    return result?.trends ?? [];
  }

  async getSources(
    startDate?: string,
    endDate?: string
  ): Promise<SourceBreakdown[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    const result = await this.request<{ sources: SourceBreakdown[] }>(`/api/v1/marketing/analytics/sources${query ? `?${query}` : ''}`);
    return result?.sources ?? [];
  }

  async getSessionJourney(sessionId: string): Promise<SessionJourney> {
    return this.request(`/api/v1/marketing/analytics/session/${sessionId}`);
  }

  // ============================================
  // GHL (GoHighLevel) INTEGRATION
  // ============================================

  async getGhlStatus(): Promise<GhlConnectionStatus> {
    return this.request('/api/v1/marketing/ghl/status');
  }

  async getGhlConnectUrl(): Promise<{ url: string }> {
    return this.request('/api/v1/marketing/ghl/connect');
  }

  async disconnectGhl(): Promise<void> {
    return this.request('/api/v1/marketing/ghl/disconnect', { method: 'DELETE' });
  }
}

// Export singleton instance
export const marketingService = new MarketingService();
export default marketingService;
